// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, browser:true, react:true*/
import React, {useContext, useReducer, useRef, useEffect,
    forwardRef} from 'react';
import styles from './premium_experience.module.less';
import cn from 'classnames';
import {Settings_context} from '../settings_ctx';
import Icon_x from '../img/svg/icon/ic-x.svg';
import {Svg, t_sprite} from './svg';
import {Button} from '../lib/button';
import {use_in_view} from '../lib/use_in_view';

interface i_premium_experience_state {
    selected_item?: number;
}

interface i_reducer_item_toggle_payload {
    idx: number;
}

interface i_reducer_action {
    type: 'item_toggle';
    payload: i_reducer_item_toggle_payload;
}

const reducer = (state: i_premium_experience_state,
    action: i_reducer_action): i_premium_experience_state=>
{
    switch (action.type)
    {
        case 'item_toggle':
            return {...state, selected_item:
                state.selected_item !== action.payload.idx
                ? action.payload.idx : undefined};
    }
};

export interface i_premium_experience_item {
    icon?: JSX.Element;
    icon_id?: string;
    icon_type?: t_sprite;
    title?: string;
    subtitle?: string;
}

interface i_premium_experience_item_props extends i_premium_experience_item {
    selected_item?: number;
    idx: number;
    dispatch: (action: i_reducer_action)=>void;
    key: React.Key;
}

interface i_premium_experience_item_header_props extends
    i_premium_experience_item {
    hidden?: boolean;
    children?: React.ReactNode;
}

const Premium_experience_item_header = forwardRef<any,
    i_premium_experience_item_header_props>((props, ref)=>{
    const {icon, icon_id, icon_type, title, hidden} = props;
    const {theme} = useContext(Settings_context);
    return <div className={cn(styles.item_header, hidden && styles.hidden)}
      ref={ref}>
      {icon && <Svg icon={icon}/>}
      {!icon && icon_id && <Svg w={40} h={40} type={icon_type||'feature'}
      id={icon_id}/>}
      <p className={cn(theme.bold, styles.item_title)}>
        {title}
      </p>
    </div>;
});

const Premium_experience_item = ({icon, icon_id, icon_type, title,
    selected_item, idx, subtitle, dispatch,
    ...props}: i_premium_experience_item_props)=>{
    const {theme} = useContext(Settings_context);
    const is_selected = selected_item === idx;
    const header_ref = useRef();
    const ref = useRef<any>();
    const is_in_view = use_in_view(header_ref);
    const handle_on_click =
        ()=>dispatch({type: 'item_toggle', payload: {idx}});
    useEffect(()=>{
        if (!is_in_view && is_selected)
            dispatch({type: 'item_toggle', payload: {idx}});
    }, [is_in_view]);
    const handle_document_click = e=>{
        if (ref.current && !ref.current.contains(e.target))
            dispatch({type: 'item_toggle', payload: {idx}});
    };
    useEffect(()=>{
        if (!is_selected)
            return;
        document.addEventListener('mousedown', handle_document_click);
        return ()=>
            document.removeEventListener('mousedown', handle_document_click);
    }, [is_selected]);
    return <div {...props} className={cn(styles.item_container,
      selected_item !== undefined && !is_selected && styles.inactive,
      is_selected && styles.unwrapped)}
      style={{'--idx': idx} as React.CSSProperties}
      onClick={handle_on_click} ref={ref}>
      <div className={cn(styles.item_unwrapped)}>
        <Premium_experience_item_header icon={icon} icon_id={icon_id}
          icon_type={icon_type} title={title}/>
        <p className={cn(theme.xs, theme.grey_2, styles.item_content,
          !is_selected && styles.hidden)}>{subtitle}</p>
        <span className={cn(styles.close_btn, !is_selected && styles.hidden)}>
          <Svg icon={<Icon_x width={16} height={16}/>}/></span>
      </div>
      <Premium_experience_item_header icon={icon} icon_id={icon_id}
        icon_type={icon_type} title={title} hidden={true} ref={header_ref}/>
    </div>;
};

interface i_premium_button_props {
    open_payment_form: ()=>void;
    starting_at_text: string;
}

const Payment_button = ({open_payment_form, starting_at_text}:
    i_premium_button_props)=>{
    return <Button caption={starting_at_text} arrow size={'md'}
      on_click={open_payment_form}
      cls={styles.payment_button}/>;
};

export interface i_premium_experience {
    title?: string;
    items?: Array<i_premium_experience_item>;
    starting_at_text: string;
    open_payment_form: ()=>void;
}

interface i_premium_experience_props extends i_premium_experience {
    key: React.Key;
    class_name?: string;
}

export const Premium_experience = ({title, items, open_payment_form,
    starting_at_text, class_name, ...props}: i_premium_experience_props)=>{
    const [{selected_item}, dispatch] = useReducer(reducer,
        {selected_item: undefined});
    return <div {...props} className={cn(styles.root, class_name)}>
      <h2>{title}</h2>
      <div className={styles.items_grid}>
        {items && items.map((item, idx)=>
          <Premium_experience_item key={idx} {...item}
            idx={items.length - idx - 1} dispatch={dispatch}
            selected_item={selected_item}/>)}
      </div>
      <Payment_button open_payment_form={open_payment_form}
        starting_at_text={starting_at_text}/>
    </div>;
};
